import { useState, useEffect } from 'react'
import api from '../../services/api'
import { useAuth } from '../../context/AuthContext'
import toast from 'react-hot-toast'
import { pedirPermiso, reproducirSonido, SONIDOS_DISPONIBLES } from '../../services/notifications'
import OneTimeSecretDialog from '../../components/OneTimeSecretDialog'
import SelectorColor from '../../components/SelectorColor'

function generarWebhookSecret() {
  const bytes = new Uint8Array(32)
  window.crypto.getRandomValues(bytes)
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
}

export default function Configuracion() {
  const { usuario, empresa, plan } = useAuth()
  const puedeVerWebhooks = ['admin', 'supervisor'].includes(usuario?.rol)
  const puedeEditarWebhooks = usuario?.rol === 'admin'
  const [tab, setTab] = useState('empresa')
  const [notifSonido, setNotifSonido] = useState(localStorage.getItem('notif_sonido') || 'ding')
  const [notifPush, setNotifPush]     = useState(localStorage.getItem('notif_push') !== 'false')
  const [permisoNotif, setPermisoNotif] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'default')
  const [form, setForm] = useState(empresa || {})
  const [tags, setTags] = useState([])
  const [campos, setCampos] = useState([])
  const [webhooks, setWebhooks] = useState([])
  const [nuevoTag, setNuevoTag] = useState({ nombre: '', color: '#128C7E' })
  const [nuevoCampo, setNuevoCampo] = useState({ nombre: '', clave: '', tipo: 'texto' })
  const [nuevoWebhook, setNuevoWebhook] = useState({ nombre: '', url: '', eventos: [], secret: '' })
  const [secretUnaVez, setSecretUnaVez] = useState(null)

  useEffect(() => {
    api.get('/contactos/tags/all').then(r => setTags(r.data))
    api.get('/contactos/campos/all').then(r => setCampos(r.data))
    if (plan?.permiteWebhooks && puedeVerWebhooks) {
      api.get('/webhooks').then(r => setWebhooks(r.data)).catch(() => setWebhooks([]))
    }
  }, [plan, puedeVerWebhooks])

  const guardarEmpresa = async () => {
    try {
      await api.put('/empresa', form)
      toast.success('Empresa actualizada')
    } catch (e) { toast.error(e.response?.data?.error || 'Error') }
  }

  const crearTag = async () => {
    try {
      const { data } = await api.post('/contactos/tags', nuevoTag)
      setTags([...tags, data])
      setNuevoTag({ nombre: '', color: '#128C7E' })
      toast.success('Etiqueta creada')
    } catch (e) { toast.error(e.response?.data?.error || 'Error') }
  }

  const eliminarTag = async (id) => {
    await api.delete(`/contactos/tags/${id}`)
    setTags(tags.filter(t => t.id !== id))
  }

  const crearCampo = async () => {
    try {
      const data = { ...nuevoCampo, clave: nuevoCampo.clave || nuevoCampo.nombre.toLowerCase().replace(/[^a-z0-9]/g, '_') }
      const { data: campo } = await api.post('/contactos/campos', data)
      setCampos([...campos, campo])
      setNuevoCampo({ nombre: '', clave: '', tipo: 'texto' })
      toast.success('Campo creado')
    } catch (e) { toast.error(e.response?.data?.error || 'Error') }
  }

  const eliminarCampo = async (id) => {
    await api.delete(`/contactos/campos/${id}`)
    setCampos(campos.filter(c => c.id !== id))
  }

  const crearWebhook = async () => {
    try {
      if (nuevoWebhook.secret.length < 32) return toast.error('El secret debe tener al menos 32 caracteres')
      const { data } = await api.post('/webhooks', nuevoWebhook)
      setWebhooks(prev => [...prev, data])
      setSecretUnaVez({ secret: nuevoWebhook.secret, titulo: 'Webhook creado: guarda el secret' })
      setNuevoWebhook({ nombre: '', url: '', eventos: [], secret: '' })
      toast.success('Webhook creado. Guarda el secret mostrado antes de continuar.')
    } catch (e) { toast.error(e.response?.data?.error || 'Error') }
  }

  const rotarWebhookSecret = async (id) => {
    const sugerido = generarWebhookSecret()
    const secret = window.prompt('Nuevo secret (cópialo antes de continuar; no volverá a mostrarse):', sugerido)
    if (secret === null) return
    if (secret.length < 32) return toast.error('El secret debe tener al menos 32 caracteres')
    try {
      const { data } = await api.post(`/webhooks/${id}/rotate-secret`, { secret })
      setWebhooks(prev => prev.map(w => w.id === id ? data : w))
      setSecretUnaVez({ secret, titulo: 'Secret rotado: guarda el nuevo valor' })
      toast.success('Secret rotado. Guarda el valor mostrado antes de continuar.')
    } catch (e) { toast.error(e.response?.data?.error || 'Error rotando secret') }
  }

  const eliminarWebhook = async (id) => {
    await api.delete(`/webhooks/${id}`)
    setWebhooks(webhooks.filter(w => w.id !== id))
  }

  const eventosDisponibles = ['mensaje_recibido', 'mensaje_enviado_bot', 'contacto_creado', 'negocio_movido', 'chat_cerrado', 'campana_completada']

  return (
    <div className="page">
      {secretUnaVez && (
        <OneTimeSecretDialog
          secret={secretUnaVez.secret}
          titulo={secretUnaVez.titulo}
          onConfirm={() => setSecretUnaVez(null)}
        />
      )}
      <h1 className="text-2xl font-bold" style={{ marginBottom: 18 }}>⚙️ Configuración</h1>

      <div className="flex gap-2 tabs-h-scroll" style={{ marginBottom: 18 }}>
        {[
          { id: 'empresa', label: '🏢 Empresa' },
          { id: 'tags', label: '🏷️ Etiquetas' },
          { id: 'campos', label: '📝 Campos' },
          { id: 'webhooks', label: '🔗 Webhooks', disabled: !plan?.permiteWebhooks || !puedeVerWebhooks },
          { id: 'notificaciones', label: '🔔 Notificaciones' },
          { id: 'plan', label: '💎 Mi plan' },
        ].map(t => (
          <button key={t.id} className={`btn ${tab === t.id ? 'btn-primary' : ''}`} disabled={t.disabled} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {tab === 'empresa' && (
        <div className="card" style={{ maxWidth: 600 }}>
          <h3 className="card-title">Datos de la empresa</h3>
          <div className="form-group"><label className="form-label">Nombre</label><input className="input" value={form.nombre || ''} onChange={e => setForm({...form, nombre: e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Email</label><input className="input" type="email" value={form.email || ''} onChange={e => setForm({...form, email: e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Teléfono</label><input className="input" value={form.telefono || ''} onChange={e => setForm({...form, telefono: e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Zona horaria</label><input className="input" value={form.zonaHoraria || ''} onChange={e => setForm({...form, zonaHoraria: e.target.value})} /></div>
          <div className="form-group">
            <label className="form-label">Color primario</label>
            <div style={{ marginTop: 6 }}>
              <SelectorColor value={form.colorPrimario || '#128C7E'} onChange={c => setForm({ ...form, colorPrimario: c })} />
            </div>
          </div>
          <button className="btn btn-primary" onClick={guardarEmpresa}>💾 Guardar</button>
        </div>
      )}

      {tab === 'tags' && (
        <div className="card" style={{ maxWidth: 700 }}>
          <h3 className="card-title">🏷️ Etiquetas de contactos</h3>
          <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div className="flex gap-2">
              <input className="input" placeholder="Nombre de la etiqueta" value={nuevoTag.nombre} onChange={e => setNuevoTag({...nuevoTag, nombre: e.target.value})} />
              <button className="btn btn-primary" onClick={crearTag}>+ Crear</button>
            </div>
            <SelectorColor value={nuevoTag.color} onChange={c => setNuevoTag({ ...nuevoTag, color: c })} />
          </div>
          <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
            {tags.map(t => (
              <div key={t.id} className="badge" style={{ background: t.color, color: 'white', padding: '6px 10px' }}>
                {t.nombre} <button onClick={() => eliminarTag(t.id)} style={{ background: 'none', border: 'none', color: 'white', marginLeft: 6, cursor: 'pointer' }}>✕</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'campos' && (
        <div className="card" style={{ maxWidth: 700 }}>
          <h3 className="card-title">📝 Campos personalizados</h3>
          <div className="flex gap-2" style={{ marginBottom: 16 }}>
            <input className="input" placeholder="Nombre (ej: Empresa)" value={nuevoCampo.nombre} onChange={e => setNuevoCampo({...nuevoCampo, nombre: e.target.value})} />
            <select className="select" style={{ width: 150 }} value={nuevoCampo.tipo} onChange={e => setNuevoCampo({...nuevoCampo, tipo: e.target.value})}>
              <option value="texto">Texto</option>
              <option value="numero">Número</option>
              <option value="fecha">Fecha</option>
              <option value="seleccion">Selección</option>
              <option value="booleano">Sí/No</option>
              <option value="url">URL</option>
              <option value="email">Email</option>
            </select>
            <button className="btn btn-primary" onClick={crearCampo}>+ Crear</button>
          </div>
          <table className="table table-card">
            <thead><tr><th>Nombre</th><th>Clave</th><th>Tipo</th><th></th></tr></thead>
            <tbody>
              {campos.map(c => (
                <tr key={c.id}>
                  <td data-label="Nombre">{c.nombre}</td>
                  <td data-label="Clave"><code>{c.clave}</code></td>
                  <td data-label="Tipo"><span className="badge">{c.tipo}</span></td>
                  <td><button className="btn btn-sm btn-ghost" onClick={() => eliminarCampo(c.id)}>🗑️</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'webhooks' && (
        <div className="card" style={{ maxWidth: 800 }}>
          <h3 className="card-title">🔗 Webhooks</h3>
          <p className="card-subtitle">Recibe notificaciones en tu sistema cuando ocurran eventos</p>

          {puedeEditarWebhooks && <div className="flex flex-col gap-2" style={{ marginBottom: 16 }}>
            <input className="input" placeholder="Nombre" value={nuevoWebhook.nombre} onChange={e => setNuevoWebhook({...nuevoWebhook, nombre: e.target.value})} />
            <input className="input" placeholder="URL del endpoint" value={nuevoWebhook.url} onChange={e => setNuevoWebhook({...nuevoWebhook, url: e.target.value})} />
            <div className="flex gap-2">
              <input className="input" type="password" placeholder="Secret (mínimo 32 caracteres)" value={nuevoWebhook.secret} onChange={e => setNuevoWebhook({...nuevoWebhook, secret: e.target.value})} />
              <button type="button" className="btn btn-ghost" onClick={() => {
                const secret = generarWebhookSecret()
                setNuevoWebhook({...nuevoWebhook, secret})
                toast.success('Secret generado. Guárdalo antes de crear el webhook.')
              }}>Generar</button>
            </div>
            <div className="text-sm text-muted">Guarda el secret antes de crear. El CRM solo mostrará si está configurado.</div>
            <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
              {eventosDisponibles.map(ev => (
                <button type="button" key={ev}
                  className={`badge ${nuevoWebhook.eventos.includes(ev) ? 'badge-primary' : ''}`}
                  style={{ cursor: 'pointer', border: '1px solid var(--border)' }}
                  onClick={() => setNuevoWebhook({...nuevoWebhook, eventos: nuevoWebhook.eventos.includes(ev) ? nuevoWebhook.eventos.filter(e => e !== ev) : [...nuevoWebhook.eventos, ev]})}>
                  {ev}
                </button>
              ))}
            </div>
            <button className="btn btn-primary" onClick={crearWebhook}>+ Crear webhook</button>
          </div>}

          {webhooks.map(w => (
            <div key={w.id} style={{ padding: 12, background: 'var(--bg)', borderRadius: 8, marginBottom: 8 }}>
              <div className="flex justify-between items-center">
                <div>
                  <div className="font-semibold">{w.nombre}</div>
                  <div className="text-sm text-muted">{w.url}</div>
                  <div style={{ marginTop: 4 }}>{w.eventos.map(e => <span key={e} className="badge" style={{ marginRight: 4 }}>{e}</span>)}</div>
                  <div className="text-sm text-muted" style={{ marginTop: 6 }}>
                    Disparos: {w.totalDisparos} · Fallos: {w.totalFallos} · Secret configurado: {w.secretSet ? 'sí' : 'no'}
                  </div>
                </div>
                {puedeEditarWebhooks && <div className="flex gap-2">
                  <button className="btn btn-sm btn-ghost" onClick={() => rotarWebhookSecret(w.id)}>Rotar secret</button>
                  <button className="btn btn-sm btn-danger" onClick={() => eliminarWebhook(w.id)}>🗑️</button>
                </div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'notificaciones' && (
        <div className="card" style={{ maxWidth: 600 }}>
          <h3 className="card-title">🔔 Notificaciones</h3>
          <p className="card-subtitle">Configura cómo te avisa el CRM cuando llegan mensajes nuevos</p>

          {/* Permiso push */}
          <div className="form-group" style={{ background: permisoNotif === 'granted' ? '#f0fdf4' : '#fefce8', border: `1px solid ${permisoNotif === 'granted' ? '#a7f3d0' : '#fde047'}`, borderRadius: 10, padding: '14px 16px', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
              <div>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>
                  {permisoNotif === 'granted' ? '✅ Notificaciones push activadas' : '⚠️ Notificaciones push desactivadas'}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  {permisoNotif === 'granted'
                    ? 'Recibirás alertas aunque la pestaña esté en segundo plano'
                    : 'Actívalas para recibir alertas cuando la pestaña no está visible'}
                </div>
              </div>
              {permisoNotif !== 'granted' && (
                <button className="btn btn-primary" onClick={async () => {
                  const p = await pedirPermiso()
                  setPermisoNotif(p)
                  if (p === 'granted') toast.success('✅ Notificaciones activadas')
                  else if (p === 'denied') toast.error('Bloqueadas en el navegador — actívalas manualmente en la barra de dirección')
                }}>
                  Activar ahora
                </button>
              )}
            </div>
          </div>

          {/* Push toggle */}
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
            <div>
              <div style={{ fontWeight: 600 }}>Notificaciones push</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Mostrar popup cuando llega un mensaje nuevo</div>
            </div>
            <div className={`toggle ${notifPush ? 'on' : ''}`} onClick={() => {
              const v = !notifPush
              setNotifPush(v)
              localStorage.setItem('notif_push', v)
            }} />
          </div>

          {/* Sonido */}
          <div className="form-group" style={{ marginTop: 20 }}>
            <label className="form-label">🔊 Sonido de notificación</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {SONIDOS_DISPONIBLES.map(s => (
                <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 10, border: `2px solid ${notifSonido === s.id ? 'var(--primary)' : 'var(--border)'}`, cursor: 'pointer', background: notifSonido === s.id ? '#f0fdf4' : 'white' }}>
                  <input type="radio" name="sonido" value={s.id} checked={notifSonido === s.id}
                    onChange={() => {
                      setNotifSonido(s.id)
                      localStorage.setItem('notif_sonido', s.id)
                      reproducirSonido(s.id)
                    }}
                    style={{ accentColor: 'var(--primary)' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{s.label}</div>
                  </div>
                  <button type="button" onClick={e => { e.preventDefault(); reproducirSonido(s.id) }}
                    style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '3px 10px', cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)' }}>
                    ▶ Probar
                  </button>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'plan' && (
        <div className="card" style={{ maxWidth: 600 }}>
          <h3 className="card-title">💎 Tu plan actual</h3>
          {plan ? (
            <>
              <div className="font-bold text-xl" style={{ color: 'var(--primary)', marginBottom: 8 }}>{plan.nombre}</div>
              <p>${plan.precio}/{plan.ciclo}</p>
              <hr style={{ margin: '14px 0', border: 'none', borderTop: '1px solid var(--border)' }} />
              <div className="flex flex-col gap-2">
                <div>📱 Números: <strong>{plan.maxNumeros}</strong></div>
                <div>🧑‍💼 Agentes: <strong>{plan.maxAgentes}</strong></div>
                <div>👥 Contactos: <strong>{plan.maxContactos.toLocaleString()}</strong></div>
                <div>💬 Mensajes/mes: <strong>{plan.maxMensajesMes.toLocaleString()}</strong></div>
                <div>📢 Campañas/mes: <strong>{plan.maxCampanasMes}</strong></div>
              </div>
              <hr style={{ margin: '14px 0', border: 'none', borderTop: '1px solid var(--border)' }} />
              <div className="flex flex-col gap-1 text-sm">
                <div>{plan.permiteCampanasMasivas ? '✅' : '❌'} Campañas masivas</div>
                <div>{plan.permitePlantillas ? '✅' : '❌'} Plantillas</div>
                <div>{plan.permiteFlujosBot ? '✅' : '❌'} Flujos de bot avanzados</div>
                <div>{plan.permiteWebhooks ? '✅' : '❌'} Webhooks</div>
                <div>{plan.permiteAPI ? '✅' : '❌'} API pública</div>
                <div>{plan.permiteIA ? '✅' : '❌'} Bot con IA Gemini</div>
              </div>
              <p className="text-sm text-muted" style={{ marginTop: 14 }}>Para cambiar tu plan, contacta al administrador del sistema.</p>
            </>
          ) : <p className="text-muted">Sin plan asignado</p>}
        </div>
      )}
    </div>
  )
}
